import { Injectable, inject } from '@angular/core';
import { ValueGetterParams } from 'ag-grid-community';
import { GridUtilsService } from './grid-utils.service';

@Injectable({
  providedIn: 'root',
})
export class GridValueGetterService {
  private gridUtils = inject(GridUtilsService);

  constructor() {}

  /**
   * Retrieves the appropriate value getter function based on column configuration.
   * @param params - Parameters containing the field to get value.
   * @param column - Column configuration object, which may specify a valueGetter key.
   * @returns Formatted value string based on the column's valueGetter key.
   */
  getValueGetter(params: any, column: Record<string, any>): unknown {
    if (column.valueGetter === 'getTextValue') {
      return this.getTextValue(params);
    } else if (column.valueGetter === 'getIntValue') {
      return this.getIntValue(params);
    } else if (column.valueGetter === 'getFloatValue') {
      return this.getFloatValue(params);
    } else if (column.valueGetter === 'getDateTimeValue') {
      return this.getDateTimeValue(params);
    }
  }

  /**
   * Retrieves a cell value as text.
   * @param params - Parameters containing data for the specific row and column.
   * @returns The text value if it exists, otherwise an empty string.
   */
  getTextValue(params: ValueGetterParams): string {
    const fieldValue = params.data[params.colDef.field as string];
    return fieldValue || '';
  }

  /**
   * Retrieves and parses a cell value as an integer.
   * @param params - Parameters containing data for the specific row and column.
   * @returns The integer value if it exists, otherwise null.
   */
  getIntValue(params: ValueGetterParams): number | null {
    const fieldValue = params.data[params.colDef.field as string];
    return fieldValue ? parseInt(fieldValue) : null;
  }

  /**
   * Retrieves and parses a cell value as a floating-point number.
   * @param params - Parameters containing data for the specific row and column.
   * @returns The float value if it exists, otherwise null.
   */
  getFloatValue(params: ValueGetterParams): number | null {
    const fieldValue = params.data[params.colDef.field as string];
    return fieldValue ? parseFloat(fieldValue) : null;
  }

  /**
   * Retrieves and parses a cell value as a date.
   * @param params - Parameters containing data for the specific row and column.
   * @returns A Date object if the value exists and is valid, otherwise null.
   */
  getDateTimeValue(params: ValueGetterParams): Date | null {
    const fieldValue = params.data[params.colDef.field as string];
    return this.gridUtils.extractDateFromParams(fieldValue);
  }
}
